import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Shield, Home, Phone, Scale, Sparkles, Send, Loader2, Bot,
  CheckCircle2, XCircle, ChevronDown, ChevronUp, Star, Activity, Hand,
  Accessibility, CalendarClock, GraduationCap, Banknote, Stethoscope,
  Plane, Car, Users, HeartHandshake, RefreshCw, Flower2, BookOpen,
  FileText, AlertTriangle, Globe, Zap, IndianRupee, Percent, Lightbulb,
  HelpCircle, BadgeCheck, TrendingUp, Umbrella, HeartPulse, User, Layers
} from "lucide-react";
import axios from "axios";
import { trackAskAI } from "@/utils/analytics";
import SectionNav from "@/components/SectionNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PA_TABS = [
  { id: "overview", label: "Start Here", icon: Star, color: "from-[#D4A017] to-yellow-500" },
  { id: "core-covers", label: "The 4 Core Covers", icon: Shield, color: "from-orange-500 to-amber-500" },
  { id: "payout-chart", label: "Payout Chart", icon: Percent, color: "from-emerald-500 to-teal-500" },
  { id: "extra-benefits", label: "Extra Benefits", icon: HeartHandshake, color: "from-pink-500 to-rose-400" },
  { id: "family", label: "Family & Premium", icon: Users, color: "from-[#EDF1F8] to-cyan-500" },
  { id: "exclusions", label: "What's NOT Covered", icon: XCircle, color: "from-red-500 to-rose-500" },
  { id: "claims", label: "Claims", icon: FileText, color: "from-cyan-500 to-[#DCE4F2]" },
  { id: "jargon", label: "Jargon Buster & FAQs", icon: BookOpen, color: "from-purple-500 to-violet-500" },
  { id: "askme", label: "Ask AI", icon: Sparkles, color: "from-[#EDF1F8] to-[#DCE4F2]" }
];

const COVER_ICONS = {
  "shield": Shield,
  "accessibility": Accessibility,
  "hand": Hand,
  "calendar-clock": CalendarClock
};

const BENEFIT_ICONS = {
  "graduation-cap": GraduationCap,
  "banknote": Banknote,
  "stethoscope": Stethoscope,
  "plane": Plane,
  "car": Car,
  "users": Users,
  "heart-handshake": HeartHandshake,
  "refresh-cw": RefreshCw,
  "flower": Flower2
};

const PILLAR_COLORS = [
  { grad: "from-red-500 to-rose-500", text: "text-rose-600", bg: "bg-rose-500/10", border: "border-rose-500/30" },
  { grad: "from-orange-500 to-amber-500", text: "text-amber-600", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  { grad: "from-emerald-500 to-teal-500", text: "text-emerald-600", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  { grad: "from-blue-500 to-cyan-500", text: "text-cyan-600", bg: "bg-cyan-500/10", border: "border-cyan-500/30" }
];

const ExpandableCard = ({ title, icon: Icon, iconColor, badge, defaultOpen = false, children, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="glass-card overflow-hidden mb-4" data-testid={testId}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 hover:bg-[#EDF1F8]/30 transition-colors">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${iconColor} flex items-center justify-center flex-shrink-0`}>
            <Icon className="h-5 w-5 text-[#1B2A4A]" />
          </div>
          <h3 className="font-bold text-[#1B2A4A] text-base sm:text-lg text-left">{title}</h3>
          {badge && <span className="px-2 py-1 text-xs font-medium bg-[#D4A017]/15 text-[#A87608] rounded-full">{badge}</span>}
        </div>
        {open ? <ChevronUp className="h-5 w-5 text-[#3A4557]" /> : <ChevronDown className="h-5 w-5 text-[#3A4557]" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
            <div className="px-5 pb-5 border-t border-[#C9D4E6] pt-4">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const PercentBar = ({ percent }) => (
  <div className="flex items-center gap-3 min-w-[120px]">
    <div className="flex-1 h-2 rounded-full bg-[#EDF1F8] overflow-hidden">
      <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-[#D4A017]" style={{ width: `${percent}%` }} />
    </div>
    <span className="text-[#A87608] font-bold text-sm w-12 text-right">{percent}%</span>
  </div>
);

// ============ TAB: OVERVIEW ("Start Here") ============
const OverviewTab = ({ plan }) => {
  const edu = plan.pa_education || {};
  return (
    <div className="space-y-6" data-testid="pa-overview-tab">
      {/* One-line summary */}
      <div className="glass-card p-6 border-l-4 border-l-[#D4A017]">
        <div className="flex items-start gap-3">
          <Lightbulb className="h-6 w-6 text-[#A87608] flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-[#1B2A4A] font-bold mb-2">This plan, in plain words</h3>
            <p className="text-[#2E3B57] leading-relaxed">{plan.simple_summary}</p>
          </div>
        </div>
      </div>

      {/* What is PA insurance */}
      {edu.what_is && (
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
              <HelpCircle className="h-5 w-5 text-[#1B2A4A]" />
            </div>
            <h3 className="font-bold text-[#1B2A4A] text-lg">{edu.what_is.title}</h3>
          </div>
          <p className="text-[#2E3B57] leading-relaxed mb-4">{edu.what_is.simple}</p>
          <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
            <p className="text-amber-700 text-sm italic">💡 {edu.what_is.analogy}</p>
          </div>
        </div>
      )}

      {/* Myths vs Reality */}
      {edu.misconceptions?.length > 0 && (
        <div>
          <h3 className="font-bold text-[#1B2A4A] text-lg mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            3 dangerous myths — busted
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            {edu.misconceptions.map((m, i) => (
              <div key={i} className="glass-card p-5" data-testid={`pa-myth-card-${i}`}>
                <div className="flex items-start gap-2 mb-3">
                  <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 font-medium text-sm">{m.myth}</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[#2E3B57] text-sm leading-relaxed">{m.reality}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Protection Triangle */}
      {edu.triangle_of_protection && (
        <div className="glass-card p-6">
          <h3 className="font-bold text-[#1B2A4A] text-lg mb-1 flex items-center gap-2">
            <Umbrella className="h-5 w-5 text-[#A87608]" />
            {edu.triangle_of_protection.title}
          </h3>
          <p className="text-[#3A4557] text-sm mb-5">Health, Term and Personal Accident cover three different risks. Missing any one leaves a hole in your family's safety net.</p>
          <div className="grid md:grid-cols-3 gap-4">
            {edu.triangle_of_protection.items.map((item, i) => {
              const icons = [HeartPulse, Umbrella, Shield];
              const Icon = icons[i] || Shield;
              const isThis = i === 2;
              return (
                <div key={i} className={`p-4 rounded-xl border ${isThis ? "bg-amber-500/10 border-amber-500/40" : "bg-[#EDF1F8]/30 border-[#C9D4E6]"}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`h-5 w-5 ${isThis ? "text-amber-600" : "text-[#2E3B57]"}`} />
                    <p className={`font-bold text-sm ${isThis ? "text-amber-700" : "text-[#1B2A4A]"}`}>{item.name}</p>
                    {isThis && <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-500/20 text-amber-700 rounded-full">THIS PLAN</span>}
                  </div>
                  <p className="text-[#2E3B57] text-xs mb-2">{item.covers}</p>
                  <p className={`text-xs ${isThis ? "text-amber-700 font-medium" : "text-[#3A4557]"}`}>→ {item.gap}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Who needs this */}
      {edu.who_needs?.length > 0 && (
        <div className="glass-card p-6">
          <h3 className="font-bold text-[#1B2A4A] text-lg mb-4 flex items-center gap-2">
            <User className="h-5 w-5 text-emerald-600" />
            Who needs this the most?
          </h3>
          <div className="space-y-3">
            {edu.who_needs.map((w, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-[#EDF1F8]/30">
                <BadgeCheck className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[#1B2A4A] font-medium text-sm">{w.who}</p>
                  <p className="text-[#3A4557] text-xs">{w.why}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Plan Variants */}
      {plan.plan_variants?.length > 0 && (
        <div className="glass-card p-6">
          <h3 className="font-bold text-[#1B2A4A] text-lg mb-1 flex items-center gap-2">
            <Layers className="h-5 w-5 text-cyan-600" />
            Plan options under this product
          </h3>
          <p className="text-[#3A4557] text-sm mb-4">The same product comes in different sizes and packagings — your advisor helps pick the right one.</p>
          <div className="space-y-3">
            {plan.plan_variants.map((v, i) => (
              <div key={i} className={`p-4 rounded-xl border ${v.highlight ? "bg-amber-500/10 border-amber-500/40" : "bg-[#EDF1F8]/30 border-[#C9D4E6]"}`} data-testid={`pa-variant-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <p className={`font-bold text-sm ${v.highlight ? "text-amber-700" : "text-[#1B2A4A]"}`}>{v.name}</p>
                  {v.highlight && <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-500/20 text-amber-700 rounded-full">SPOTLIGHT</span>}
                </div>
                <p className="text-[#2E3B57] text-xs leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Why choose */}
      {plan.why_choose?.length > 0 && (
        <div className="glass-card p-6">
          <h3 className="font-bold text-[#1B2A4A] text-lg mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[#A87608]" />
            Why advisors recommend this plan
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {plan.why_choose.map((reason, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#A87608] flex-shrink-0 mt-0.5" />
                <p className="text-[#2E3B57] text-sm">{reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============ TAB: CORE COVERS ============
const CoreCoversTab = ({ plan }) => (
  <div className="space-y-6" data-testid="pa-core-covers-tab">
    <div className="glass-card p-5 border-l-4 border-l-amber-500">
      <p className="text-[#2E3B57] text-sm leading-relaxed">
        Every Personal Accident policy stands on <span className="text-amber-700 font-bold">4 pillars</span>.
        Understand these 4, and you understand 90% of PA insurance. Each pillar answers one question:
        <span className="text-[#1B2A4A] font-medium"> "What happens to my income in this situation?"</span>
      </p>
    </div>
    {plan.core_covers?.map((cover, i) => {
      const Icon = COVER_ICONS[cover.icon] || Shield;
      const c = PILLAR_COLORS[i % PILLAR_COLORS.length];
      return (
        <div key={cover.id} className="glass-card overflow-hidden" data-testid={`pa-core-cover-${cover.id}`}>
          <div className="p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-4">
              <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${c.grad} flex items-center justify-center flex-shrink-0`}>
                <Icon className="h-6 w-6 text-[#1B2A4A]" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-bold ${c.text}`}>PILLAR {i + 1}</span>
                </div>
                <h3 className="font-bold text-[#1B2A4A] text-lg">{cover.name}</h3>
              </div>
              <div className={`px-3 py-1.5 rounded-full ${c.bg} border ${c.border} self-start sm:self-center`}>
                <span className={`text-sm font-bold ${c.text}`}>{cover.payout}</span>
              </div>
            </div>
            <p className="text-[#2E3B57] leading-relaxed mb-4">{cover.simple_explanation}</p>
            {cover.example && (
              <div className={`p-4 rounded-xl ${c.bg} border ${c.border} mb-4`}>
                <p className={`text-xs font-bold ${c.text} mb-2 uppercase tracking-wide`}>Real-life example</p>
                <p className="text-[#2E3B57] text-sm leading-relaxed mb-2">{cover.example.story}</p>
                <div className="flex items-center gap-2">
                  <IndianRupee className={`h-4 w-4 ${c.text}`} />
                  <span className={`font-bold text-sm ${c.text}`}>{cover.example.payout}</span>
                </div>
              </div>
            )}
            <div className="space-y-2">
              {cover.details?.map((d, j) => (
                <div key={j} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[#3A4557] text-sm">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

// ============ TAB: PAYOUT CHART ============
const PayoutChartTab = ({ plan }) => (
  <div className="space-y-6" data-testid="pa-payout-chart-tab">
    {plan.worked_example && (
      <div className="glass-card p-6">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-1 flex items-center gap-2">
          <Zap className="h-5 w-5 text-[#A87608]" />
          {plan.worked_example.title}
        </h3>
        <p className="text-[#3A4557] text-sm mb-4">{plan.worked_example.note}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#C9D4E6]">
                <th className="text-left py-3 text-[#3A4557] font-medium">If this happens...</th>
                <th className="text-center py-3 text-[#3A4557] font-medium w-20">% Paid</th>
                <th className="text-right py-3 text-[#3A4557] font-medium">You / family get</th>
              </tr>
            </thead>
            <tbody>
              {plan.worked_example.rows.map((row, i) => (
                <tr key={i} className="border-b border-[#C9D4E6]/50 last:border-0">
                  <td className="py-3 text-[#2E3B57]">{row.scenario}</td>
                  <td className="py-3 text-center text-amber-600 font-bold">{row.percent}</td>
                  <td className="py-3 text-right text-emerald-600 font-bold">{row.payout}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}

    {plan.disablement_table?.length > 0 && (
      <ExpandableCard title="Full Permanent Disablement Table" icon={Accessibility} iconColor="from-orange-500 to-amber-500" badge="% of Sum Insured" defaultOpen testId="pa-disablement-table">
        <div className="space-y-3">
          {plan.disablement_table.map((row, i) => (
            <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 py-2 border-b border-[#C9D4E6]/50 last:border-0">
              <p className="text-[#2E3B57] text-sm flex-1">{row.loss}{row.note ? <span className="text-[#3A4557] text-xs block">{row.note}</span> : null}</p>
              <div className="w-full sm:w-48"><PercentBar percent={row.percent} /></div>
            </div>
          ))}
        </div>
      </ExpandableCard>
    )}

    {plan.broken_bones_table && (
      <ExpandableCard title="Broken Bones Payout Table" icon={Activity} iconColor="from-cyan-500 to-blue-500" badge="Optional Cover" testId="pa-bones-table">
        <p className="text-[#3A4557] text-sm mb-4">{plan.broken_bones_table.note}</p>
        <div className="space-y-3">
          {plan.broken_bones_table.rows.map((row, i) => (
            <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 py-2 border-b border-[#C9D4E6]/50 last:border-0">
              <p className="text-[#2E3B57] text-sm flex-1">{row.injury}</p>
              <div className="w-full sm:w-48"><PercentBar percent={row.percent} /></div>
            </div>
          ))}
        </div>
      </ExpandableCard>
    )}

    {plan.burns_table && (
      <ExpandableCard title="Burns Payout Table" icon={AlertTriangle} iconColor="from-red-500 to-orange-500" badge="Optional Cover" testId="pa-burns-table">
        <p className="text-[#3A4557] text-sm mb-4">{plan.burns_table.note}</p>
        <div className="space-y-3">
          {plan.burns_table.rows.map((row, i) => (
            <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 py-2 border-b border-[#C9D4E6]/50 last:border-0">
              <p className="text-[#2E3B57] text-sm flex-1">{row.injury}</p>
              <div className="w-full sm:w-48"><PercentBar percent={row.percent} /></div>
            </div>
          ))}
        </div>
      </ExpandableCard>
    )}
  </div>
);

// ============ TAB: EXTRA BENEFITS ============
const ExtraBenefitsTab = ({ plan }) => (
  <div className="space-y-6" data-testid="pa-extra-benefits-tab">
    <div className="glass-card p-5 border-l-4 border-l-pink-500">
      <p className="text-[#2E3B57] text-sm leading-relaxed">
        Beyond the 4 core covers, this plan packs thoughtful extras that solve real problems a family
        faces after an accident — school fees, hospital cash, a driver during recovery, even air ambulance.
      </p>
    </div>
    <div className="grid sm:grid-cols-2 gap-4">
      {plan.additional_benefits?.map((b, i) => {
        const Icon = BENEFIT_ICONS[b.icon] || HeartHandshake;
        return (
          <div key={i} className="glass-card p-5" data-testid={`pa-benefit-card-${i}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-400 flex items-center justify-center flex-shrink-0">
                <Icon className="h-5 w-5 text-[#1B2A4A]" />
              </div>
              <div>
                <h4 className="text-[#1B2A4A] font-bold text-sm">{b.name}</h4>
                <p className="text-[#A87608] text-xs font-medium">{b.limit}</p>
              </div>
            </div>
            <p className="text-[#3A4557] text-sm leading-relaxed">{b.simple_explanation}</p>
          </div>
        );
      })}
    </div>

    {plan.optional_covers?.length > 0 && (
      <div className="glass-card p-6">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-1">Optional covers you can add</h3>
        <p className="text-[#3A4557] text-sm mb-4">Customize the policy by adding these at purchase — ask your advisor which ones fit your life.</p>
        <div className="space-y-3">
          {plan.optional_covers.map((o, i) => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-[#EDF1F8]/30">
              <div className="h-6 w-6 rounded-full bg-[#D4A017]/15 border border-[#D4A017]/30 flex items-center justify-center flex-shrink-0">
                <span className="text-[#A87608] text-xs font-bold">+</span>
              </div>
              <div>
                <p className="text-[#1B2A4A] font-medium text-sm">{o.name}</p>
                <p className="text-[#3A4557] text-xs">{o.simple_explanation}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

// ============ TAB: FAMILY & PREMIUM ============
const FamilyPremiumTab = ({ plan }) => {
  const fam = plan.family_coverage || {};
  const prem = plan.premium_info || {};
  const care = plan.policy_care || {};
  return (
    <div className="space-y-6" data-testid="pa-family-tab">
      <div className="glass-card p-6">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-cyan-600" />
          {fam.title || "Who can be covered"}
        </h3>
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          {fam.members?.map((m, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-[#EDF1F8]/30">
              <BadgeCheck className="h-5 w-5 text-cyan-600 flex-shrink-0" />
              <div>
                <p className="text-[#1B2A4A] font-medium text-sm">{m.who}</p>
                <p className="text-[#3A4557] text-xs">{m.detail}</p>
              </div>
            </div>
          ))}
        </div>
        {fam.note && <p className="text-[#3A4557] text-sm italic">{fam.note}</p>}
      </div>

      {plan.worldwide_cover && (
        <div className="glass-card p-5 flex items-start gap-3 border-l-4 border-l-emerald-500">
          <Globe className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-[#1B2A4A] font-bold text-sm mb-1">Worldwide, 24x7 protection</h4>
            <p className="text-[#2E3B57] text-sm">{plan.worldwide_cover}</p>
          </div>
        </div>
      )}

      <div className="glass-card p-6">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-2 flex items-center gap-2">
          <IndianRupee className="h-5 w-5 text-[#A87608]" />
          How the premium works
        </h3>
        <p className="text-[#2E3B57] text-sm mb-4">{prem.note}</p>
        {prem.sample_premiums?.length > 0 && (
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            {prem.sample_premiums.map((s, i) => (
              <div key={i} className="p-4 rounded-xl bg-[#D4A017]/10 border border-[#D4A017]/30 text-center">
                <p className="text-[#3A4557] text-xs mb-1">{s.label}</p>
                <p className="text-[#A87608] font-bold">{s.premium}</p>
              </div>
            ))}
          </div>
        )}
        {prem.city_tiers?.length > 0 && (
          <div className="mb-4">
            <p className="text-[#1B2A4A] font-medium text-sm mb-2">City zones (affects premium)</p>
            <div className="space-y-2">
              {prem.city_tiers.map((t, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <span className="px-2 py-0.5 rounded-full bg-[#EDF1F8] text-[#2E3B57] text-xs font-medium w-16 text-center">{t.tier}</span>
                  <span className="text-[#3A4557]">{t.cities}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <p className="text-[#3A4557] text-sm"><span className="text-[#1B2A4A] font-medium">Payment modes:</span> {prem.payment_modes}</p>
      </div>

      <div className="glass-card p-6">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-4">Policy care — the fine print, simplified</h3>
        <div className="space-y-3">
          {care.free_look && (
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-[#2E3B57] text-sm"><span className="text-[#1B2A4A] font-medium">Free look: </span>{care.free_look}</p>
            </div>
          )}
          {care.grace_period && (
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-[#2E3B57] text-sm"><span className="text-[#1B2A4A] font-medium">Grace period: </span>{care.grace_period}</p>
            </div>
          )}
          {care.renewal && (
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-[#2E3B57] text-sm"><span className="text-[#1B2A4A] font-medium">Renewal: </span>{care.renewal}</p>
            </div>
          )}
          {care.tax_note && (
            <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800 text-sm">{care.tax_note}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ TAB: EXCLUSIONS ============
const ExclusionsTab = ({ plan }) => (
  <div className="space-y-6" data-testid="pa-exclusions-tab">
    {plan.exclusions_note && (
      <div className="glass-card p-5 border-l-4 border-l-emerald-500 flex items-start gap-3">
        <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
        <p className="text-[#2E3B57] text-sm leading-relaxed">{plan.exclusions_note}</p>
      </div>
    )}
    <div className="glass-card p-5 border-l-4 border-l-red-500">
      <p className="text-[#2E3B57] text-sm leading-relaxed">
        <span className="text-[#1B2A4A] font-bold">Honest advisor talk:</span> every insurance policy has situations
        where it won't pay. Knowing them upfront means no nasty surprises at claim time. Here they are, in plain language:
      </p>
    </div>
    <div className="grid sm:grid-cols-2 gap-4">
      {plan.exclusions?.map((e, i) => (
        <div key={i} className="glass-card p-5" data-testid={`pa-exclusion-card-${i}`}>
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <h4 className="text-[#1B2A4A] font-bold text-sm">{e.title}</h4>
          </div>
          <p className="text-[#3A4557] text-sm leading-relaxed">{e.simple_explanation}</p>
        </div>
      ))}
    </div>
  </div>
);

// ============ TAB: CLAIMS ============
const ClaimsTab = ({ plan }) => {
  const proc = plan.claim_process || {};
  return (
    <div className="space-y-6" data-testid="pa-claims-tab">
      <div className="glass-card p-6">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-6">How to claim — 4 simple steps</h3>
        <div className="space-y-6">
          {proc.steps?.map((s, i) => (
            <div key={i} className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#D4A017] to-yellow-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#1B2A4A] font-bold">{s.step}</span>
                </div>
                {i < proc.steps.length - 1 && <div className="w-0.5 flex-1 bg-[#EDF1F8] mt-2" />}
              </div>
              <div className="pb-2">
                <h4 className="text-[#1B2A4A] font-bold text-sm mb-1">{s.title}</h4>
                <p className="text-[#3A4557] text-sm leading-relaxed">{s.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {plan.claim_settlement_time && (
        <div className="glass-card p-5 flex items-start gap-3 border-l-4 border-l-emerald-500">
          <CalendarClock className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-[#1B2A4A] font-bold text-sm mb-1">Settlement promise (by IRDAI regulation)</h4>
            <p className="text-[#2E3B57] text-sm">{plan.claim_settlement_time}</p>
          </div>
        </div>
      )}
      {proc.advisor_tip && (
        <div className="glass-card p-5 flex items-start gap-3 border-l-4 border-l-[#D4A017]">
          <Lightbulb className="h-6 w-6 text-[#A87608] flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-[#1B2A4A] font-bold text-sm mb-1">Advisor's tip</h4>
            <p className="text-[#2E3B57] text-sm">{proc.advisor_tip}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ TAB: JARGON & FAQs ============
const JargonTab = ({ plan }) => {
  const [openFaq, setOpenFaq] = useState(null);
  return (
    <div className="space-y-6" data-testid="pa-jargon-tab">
      <div className="glass-card p-6">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-1 flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-purple-600" />
          Jargon Buster
        </h3>
        <p className="text-[#3A4557] text-sm mb-4">Insurance words, translated into human language.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {plan.jargon_buster?.map((j, i) => (
            <div key={i} className="p-4 rounded-xl bg-[#EDF1F8]/30">
              <p className="text-purple-700 font-bold text-sm mb-1">{j.term}</p>
              <p className="text-[#2E3B57] text-xs leading-relaxed">{j.meaning}</p>
            </div>
          ))}
        </div>
      </div>

      {plan.faqs?.length > 0 && (
        <div className="glass-card p-6">
          <h3 className="font-bold text-[#1B2A4A] text-lg mb-4 flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-cyan-600" />
            Questions everyone asks
          </h3>
          <div className="space-y-3">
            {plan.faqs.map((f, i) => (
              <div key={i} className="border border-[#C9D4E6] rounded-xl overflow-hidden" data-testid={`pa-faq-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-4 text-left hover:bg-[#EDF1F8]/30 transition-colors">
                  <span className="text-[#1B2A4A] font-medium text-sm pr-4">{f.q}</span>
                  {openFaq === i ? <ChevronUp className="h-4 w-4 text-[#3A4557] flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-[#3A4557] flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-4 pb-4">
                    <p className="text-[#2E3B57] text-sm leading-relaxed">{f.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============ TAB: ASK AI ============
const AskAITab = ({ plan }) => {
  const [question, setQuestion] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const endRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/plans/${plan.id}/ask/suggestions`)
      .then(r => setSuggestions(r.data.suggestions || []))
      .catch(() => {});
  }, [plan.id]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [history]);

  const ask = async (q = question) => {
    if (!q.trim() || loading) return;
    setHistory(prev => [...prev, { type: "user", text: q }]);
    setQuestion("");
    setLoading(true);
    trackAskAI(plan.id, q);
    try {
      const res = await axios.post(`${API}/plans/${plan.id}/ask`, {
        plan_id: plan.id, question: q, session_id: sessionId
      });
      setSessionId(res.data.session_id);
      setHistory(prev => [...prev, { type: "ai", text: res.data.answer }]);
    } catch {
      setHistory(prev => [...prev, { type: "ai", text: "Sorry, I couldn't process that question. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass-card p-4 sm:p-6" data-testid="pa-askme-tab">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#EDF1F8] to-[#DCE4F2] flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-[#A87608]" />
        </div>
        <div>
          <h3 className="font-bold text-[#1B2A4A]">Ask anything about this plan</h3>
          <p className="text-[#3A4557] text-xs">AI advisor trained on the full policy details — answers in simple language</p>
        </div>
      </div>

      {history.length === 0 && suggestions.length > 0 && (
        <div className="mb-4">
          <p className="text-[#3A4557] text-xs mb-2">Popular questions:</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.slice(0, 6).map((s, i) => (
              <button key={i} onClick={() => ask(s)} className="px-3 py-1.5 rounded-full bg-[#EDF1F8]/50 border border-[#C9D4E6] text-[#2E3B57] text-xs hover:border-[#D4A017]/50 hover:text-[#1B2A4A] transition-colors" data-testid={`pa-ask-suggestion-${i}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4 mb-4 max-h-[45vh] overflow-y-auto">
        {history.map((msg, i) => (
          <div key={i} className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] p-3 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
              msg.type === "user" ? "bg-[#D4A017]/15 text-[#A87608] border border-[#D4A017]/30" : "bg-[#EDF1F8]/40 text-[#2E3B57] border border-[#C9D4E6]"
            }`}>
              {msg.type === "ai" && <Bot className="h-4 w-4 text-[#A87608] mb-1" />}
              {msg.text}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-[#EDF1F8]/40 border border-[#C9D4E6] p-3 rounded-xl">
              <Loader2 className="h-5 w-5 text-[#A87608] animate-spin" />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder="e.g. How much cover should I take?"
          className="flex-1 px-4 py-2.5 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-sm placeholder:text-[#3A4557] focus:outline-none focus:border-[#D4A017]/50"
          data-testid="pa-ask-input"
        />
        <Button onClick={() => ask()} disabled={loading || !question.trim()} className="btn-gold px-4" data-testid="pa-ask-submit-button">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// ============ MAIN COMPONENT ============
export default function PAPlanDetail({ plan }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  const heroStats = [
    { label: "Cover up to", value: plan.sum_insured_options?.[plan.sum_insured_options.length - 1] || "₹5 Crore", icon: Shield },
    { label: "Coverage starts", value: "Day 1 — no waiting", icon: Zap },
    { label: "Where covered", value: "Worldwide 24x7", icon: Globe },
    ...(plan.irdai_data?.csr ? [{ label: "Claim Settlement Ratio", value: `${plan.irdai_data.csr}%`, icon: BadgeCheck }] : [])
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] pb-16" data-testid="pa-plan-detail-page">
      {/* Header */}
      <header className="glass-nav sticky top-2 mx-2 sm:mx-6 z-50 px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between rounded-xl sm:rounded-2xl">
        <div className="flex items-center gap-1 sm:gap-3">
          <Button variant="ghost" onClick={() => navigate("/")} className="text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]/40 px-2 sm:px-3" data-testid="pa-home-button">
            <Home className="h-4 w-4" />
          </Button>
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]/40 px-2 sm:px-3" data-testid="pa-back-button">
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline ml-2">Back</span>
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate(`/compare?plans=${plan.id}`)} className="text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]/40 px-2 sm:px-3" title="Compare" data-testid="pa-compare-button">
            <Scale className="h-4 w-4" />
            <span className="hidden sm:inline ml-2">Compare</span>
          </Button>
          <a href="tel:+919510020907">
            <Button className="btn-gold h-8 sm:h-10 px-3 sm:px-5 text-xs sm:text-sm" data-testid="pa-call-advisor-button">
              <Phone className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
              <span className="hidden sm:inline">Talk to Advisor</span>
            </Button>
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />
          <div className="flex items-start gap-4 mb-4">
            <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-white p-2 flex items-center justify-center flex-shrink-0">
              <img src={plan.company_logo} alt={plan.company} className="max-h-full max-w-full object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-700 text-xs font-bold" data-testid="pa-type-badge">
                  PERSONAL ACCIDENT
                </span>
                <span className="text-[#3A4557] text-xs">{plan.company_short || plan.company}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#1B2A4A] mb-1" data-testid="pa-plan-name">{plan.name}</h1>
              <p className="text-[#A87608] text-sm font-medium">{plan.tagline}</p>
            </div>
          </div>
          <p className="text-[#2E3B57] text-sm sm:text-base leading-relaxed mb-6 max-w-3xl">{plan.description}</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {heroStats.map((s, i) => (
              <div key={i} className="p-3 rounded-xl bg-[#EDF1F8]/40 border border-[#C9D4E6]">
                <div className="flex items-center gap-2 mb-1">
                  <s.icon className="h-4 w-4 text-amber-600" />
                  <p className="text-[#3A4557] text-xs">{s.label}</p>
                </div>
                <p className="text-[#1B2A4A] font-bold text-sm">{s.value}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Tabs */}
      <div className="sticky top-16 sm:top-20 z-40 bg-white/95 backdrop-blur-md py-2 border-b border-[#C9D4E6]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <SectionNav tabs={PA_TABS} activeTab={activeTab} onChange={setActiveTab} testIdPrefix="pa-tab" />
        </div>
      </div>

      {/* Tab Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.15 }}>
            {activeTab === "overview" && <OverviewTab plan={plan} />}
            {activeTab === "core-covers" && <CoreCoversTab plan={plan} />}
            {activeTab === "payout-chart" && <PayoutChartTab plan={plan} />}
            {activeTab === "extra-benefits" && <ExtraBenefitsTab plan={plan} />}
            {activeTab === "family" && <FamilyPremiumTab plan={plan} />}
            {activeTab === "exclusions" && <ExclusionsTab plan={plan} />}
            {activeTab === "claims" && <ClaimsTab plan={plan} />}
            {activeTab === "jargon" && <JargonTab plan={plan} />}
            {activeTab === "askme" && <AskAITab plan={plan} />}
          </motion.div>
        </AnimatePresence>

        {/* CTA */}
        <div className="glass-card p-6 text-center mt-8 bg-gradient-to-r from-amber-500/5 to-[#D4A017]/10">
          <h3 className="text-xl font-bold text-[#1B2A4A] mb-2">Ready to protect your family's income?</h3>
          <p className="text-[#3A4557] mb-4 text-sm">Get a personalized quote based on your occupation, city and income — in minutes.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="tel:+919510020907">
              <Button className="btn-gold" data-testid="pa-cta-call-button">
                <Phone className="h-4 w-4 mr-2" />
                Talk to Advisor
              </Button>
            </a>
            <Button onClick={() => navigate(`/compare?plans=${plan.id}`)} variant="outline" className="border-[#C9D4E6] text-[#2E3B57]" data-testid="pa-cta-compare-button">
              <Scale className="h-4 w-4 mr-2" />
              Compare Plans
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
